import { FC, Fragment, useState } from 'react';
import dayjs from 'dayjs';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Models
import { IExecution } from 'app/store/AutomatedWorkflowsExecutions/AutomatedWorkflowsExecutions.models';
// Types
import { ContentType } from 'app/types/ContentItem';
// Selectors
import { selectDocuments } from 'app/store/DMSDocuments/DMSDocuments.selectors';
import { selectReports } from 'app/store/CaseReports/CaseReports.selectors';
// Mui
import { Theme, Paper, Box, Divider, Typography, Collapse, Link } from '@mui/material';
// Icons
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNewOutlined as OpenInNewOutlinedIcon
} from '@mui/icons-material';
// Compoennts
import { InfoBlock } from 'app/components/Utilities';
import { Button } from './Mui';
// Utilities
import { statusThemeColor } from 'app/utilities/Utilities';
import { getDurationTimeAsText } from 'app/utilities/DateTime';
import { parseContentItem } from 'app/utilities/ContentItemUtils';

type Props = {
  execution: IExecution;
  showWorkflowName?: boolean;
  showDocuments?: boolean;
}

const WorkflowExecutionCard:FC<Props> = ({
  // Props
  execution, showWorkflowName = true, showDocuments = false
}) => {
  const { t } = useTranslation();

  const durationTimeAsText = getDurationTimeAsText(execution.startedOn, execution.completedOn);
  const documents = useAppSelector(selectDocuments);
  const reports = useAppSelector(selectReports);
  const [openPanel, setOpenPanel] = useState<'parameters' | 'artifacts' | null>(null);

  const executionName = showWorkflowName
    ? execution.workflow.name
    : execution.case.name
  ;

  const renderDocumentLink = (docId:number) => {
    const document = documents?.find((doc) => doc.id === docId);
    if (!document) return null;
    const href = `/admin/cases/${execution.case.id}/documents/${docId}`;
    return (
      <ComponentLink
        key={`document-${docId}`}
        href={href}
        text={document.name}
      />
    )
  }

  const renderReportLink = (reportId:number) => {
    const report = reports?.find((report) => report.id === reportId);
    if (!report) return null;
    const href = `/admin/cases/${execution.case.id}/reports?reportId=${reportId}`;
    return (
      <ComponentLink
        key={`report-${reportId}`}
        href={href}
        text={report.name}
      />
    )
  }

  const handleTogglePanel = (panel: 'parameters' | 'artifacts') => {
    setOpenPanel((prev) => (prev === panel ? null : panel));
  }

  const [artifactsDocuments, artifactsReports] = (execution.artifacts || []).reduce((acc:any, urn) => {
    const [type, id] = parseContentItem(urn);
    if (type === ContentType.Document) {
      acc[0].push(id);
    } else if (type === ContentType.Report) {
      acc[1].push(id);
    }
    return acc;
  }, [[], []]);

  const showDocumentAndArtefactSection = showDocuments && (
    !!execution.parameters?.documentIds || !!execution.artifacts
  );

  return (
    <Paper sx={{ p: 4 }}>
      <Typography sx={{ mb: 2 }} variant="h6">{executionName}</Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <InfoBlock
          label={t('labels.status')}
          value={
            <Typography
              sx={{
                display: 'block',
                color: (theme:Theme) => statusThemeColor(execution.status, theme),
                fontWeight: 700,
                textTransform: 'capitalize'
              }}
              variant="caption"
            >{execution.status}</Typography>
          }
          direction="row"
        />
        {durationTimeAsText ? (
          <InfoBlock
            label={t('labels.duration')}
            value={durationTimeAsText}
            direction="row"
          />
        ) : null}
      </Box>
      {execution.startedOn || execution.completedOn ? (
        <Fragment>
          <Divider sx={{ borderColor: 'rgba(33,33,33,0.08)', my: 4 }} />
          <Box sx={{ display: 'flex', gap: 4 }}>
            {execution.startedOn ? (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <InfoBlock
                  label={t('labels.startedOn')}
                  value={dayjs(execution.startedOn).local().format('MMMM DD, YYYY, hh:mm a')}
                />
              </Box>
            ) : null}
            {execution.completedOn ? (
              <InfoBlock
                label={t('labels.completedOn')}
                value={dayjs(execution.completedOn).local().format('MMMM DD, YYYY, hh:mm a')}
              />
            ) : null}
          </Box>
        </Fragment>
      ) : null}
      {showDocumentAndArtefactSection ? (
        <Fragment>
          <Divider sx={{ borderColor: 'rgba(33,33,33,0.08)', my: 4 }} />
          <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
            {!!execution.parameters?.documentIds?.length ? (
              <Button
                name="open-parameters-panel"
                size="small"
                sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                onClick={() => handleTogglePanel('parameters')}
                endIcon={openPanel === 'parameters' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {t('buttons.parameters')}
              </Button>
            ) : null}
            {!!execution.artifacts?.length ? (
              <Button
                name="open-artifacts-panel"
                size="small"
                sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                onClick={() => handleTogglePanel('artifacts')}
                endIcon={openPanel === 'artifacts' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {t('buttons.artifacts')}
              </Button>
            ) : null}
          </Box>

          <Collapse in={openPanel === 'parameters'}>
            {execution.parameters?.documentIds?.length ? (
              <Box sx={{
                mt: 2,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ padding: '4px 8px 4px 16px', bgcolor: 'rgba(247, 246, 248, 1)' }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 500, color: 'rgba(0, 0, 0, 0.6)'
                  }}>{t('labels.documents')}</Typography>
                </Box>
                {execution.parameters.documentIds.map((docId) => renderDocumentLink(docId))}
              </Box>
            ) : null}
          </Collapse>

          <Collapse in={openPanel === 'artifacts'}>
            {artifactsDocuments.length ? (
              <Box sx={{
                mt: 2,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ padding: '4px 8px 4px 16px', bgcolor: 'rgba(247, 246, 248, 1)' }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 500, color: 'rgba(0, 0, 0, 0.6)'
                  }}>{t('labels.documents')}</Typography>
                </Box>
                {artifactsDocuments.map((docId:number) => renderDocumentLink(docId))}
              </Box>
            ) : null}
            {artifactsReports.length ? (
              <Box sx={{
                mt: 2,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ padding: '4px 8px 4px 16px', bgcolor: 'rgba(247, 246, 248, 1)' }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 500, color: 'rgba(0, 0, 0, 0.6)'
                  }}>{t('labels.reports')}</Typography>
                </Box>
                {artifactsReports.map((reportId:number) => renderReportLink(reportId))}
              </Box>
            ) : null}
          </Collapse>
        </Fragment>
      ) : null}
    </Paper>
  )
}

export default WorkflowExecutionCard

// Component Link
const ComponentLink = ({ href, text }: { href:string, text:string }) => (
  <Link
    href={href}
    underline="none"
    target="_blank"
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      p: 2,
      color: 'rgba(0, 0, 0, 0.87)',
      '& + &': {
        borderTop: '1px solid rgba(0, 0, 0, 0.12)'
      },
      '&:hover': (theme:Theme) => ({
        color: theme.palette.primary.main,
        '& svg': {
          fill: theme.palette.primary.main
        }
      })
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap={true}>{text}</Typography>
    <OpenInNewOutlinedIcon sx={{ fill: 'rgba(0, 0, 0, 0.54)' }} fontSize="small" />
  </Link>
) 